import {
  QUESTIONNAIRE_DRAFTED_REPLY,
  QUESTIONNAIRE_NOTHING_CHANGED_REPLY,
  QUESTIONNAIRE_UPDATED_REPLY,
} from "@digital-twins/shared-types";
import type {
  ChatMessageCollect,
  ChatMessageOption,
  QuestionnaireClarification,
} from "@digital-twins/shared-types";
import type { TFunction } from "i18next";

/**
 * Builder cards in the interface language. Nest asks in English (its copy is
 * model-facing), and an opening-chain card's clarification record names the
 * step that asked it (`origin: 'chain'`, `code`). The builder words that card
 * from its code, `questionnaire.opening.<code>.question`, and each option from
 * its stable id, `questionnaire.opening.<code>.options.<id>`. A count comes
 * from the card's own typed `collect.maxAssets`, and the noun from its
 * `collect.assetType` (`questionnaire.assetTypes.<type>.plural`). A card no chain step asked (a
 * model's or a gate's question, a chat stored before the records) keeps its
 * own words.
 *
 * Cache scope: one-shot display copy; not an LLM prompt.
 */

/** The opening-chain step that asked each card, by the card's message id. */
export function chainCodesByMessage(
  clarifications: ReadonlyArray<QuestionnaireClarification>,
): Map<string, string> {
  const codes = new Map<string, string>();
  for (const record of clarifications) {
    if (record.origin === "chain" && record.code && record.messageId) {
      codes.set(record.messageId, record.code);
    }
  }
  return codes;
}

/**
 * The fixed lines no chain step asks: a rejected chat turn's own line (F16),
 * the line a committed change or first Build keeps when its reply could not
 * be written (round 3 A2), and the generic greeting a goal-less session opens
 * with.
 */
const FIXED_LINE_KEYS: Readonly<Record<string, string>> = {
  [QUESTIONNAIRE_NOTHING_CHANGED_REPLY]: "questionnaire.notDone.nothingChanged",
  [QUESTIONNAIRE_UPDATED_REPLY]: "questionnaire.reply.updated",
  [QUESTIONNAIRE_DRAFTED_REPLY]: "questionnaire.reply.drafted",
  "What are you interested in learning from this study?":
    "questionnaire.opening.defaultGreeting",
};

type BuilderTranslate = (key: string, options?: Record<string, unknown>) => string;

/** A card's question; `chainCode` is the step its record names, if any. */
export function builderCardText(
  message: { content: string; collect?: ChatMessageCollect | null },
  chainCode: string | null | undefined,
  translate: TFunction<"app">,
): string {
  // The keys are built from the step's code, not the typed key union.
  const t = translate as unknown as BuilderTranslate;
  // An attach card's question is worded from its items (F19): its content is
  // not the step's copy.
  if (chainCode && !message.collect?.attachTo) {
    const assetType = message.collect?.assetType;
    return t(`questionnaire.opening.${chainCode}.question`, {
      count: message.collect?.maxAssets,
      // The family's noun, mid-sentence: "Add up to 3 ads".
      plural: assetType
        ? t(`questionnaire.assetTypes.${assetType}.plural`).toLocaleLowerCase()
        : undefined,
    });
  }
  const fixedKey = FIXED_LINE_KEYS[message.content];
  return fixedKey ? t(fixedKey) : message.content;
}

/**
 * An option's position mark, by its index on the card: A to Z, then AA, AB and
 * on. One scheme however many options a card offers (an attach card lists one
 * per item); the composer, a locked card and the free-text row all read it.
 */
export function builderOptionMark(index: number): string {
  let mark = "";
  for (let rest = index + 1; rest > 0; rest = Math.floor((rest - 1) / 26)) {
    mark = String.fromCharCode(65 + ((rest - 1) % 26)) + mark;
  }
  return mark;
}

/** An option's label; a chain card's option is worded from its id. */
export function builderOptionLabel(
  option: Pick<ChatMessageOption, "id" | "label">,
  chainCode: string | null | undefined,
  translate: TFunction<"app">,
): string {
  const t = translate as unknown as BuilderTranslate;
  return chainCode && option.id
    ? t(`questionnaire.opening.${chainCode}.options.${option.id}`)
    : option.label;
}
